import { isMergeMetricSource, MERGE_TABLE_NAME, } from '../../types/mergeQuery';
import { getItemId } from '../../utils/item';
// Merged output columns are fields of the merge/source "tables", so getItemId
// is the naming authority.
const mergedColumnId = (table, name) => getItemId({ table, name });
/**
 * Renames the AI's free-form source and join-key names to the names the
 * merge editor would give them, so everything downstream treats an AI merge
 * exactly like one built by hand. Chart configs referencing the AI's merged column ids must
 * be remapped with `fieldIdByAiFieldId`.
 */
export const canonicalizeAiMerge = (mergeQuery) => {
    if (mergeQuery.sources.length !== 2)
        return null;
    // AI merges are built from metric queries; a merge over existing results
    // has no canonical editor form to rename into.
    const metricSources = mergeQuery.sources.filter(isMergeMetricSource);
    if (metricSources.length !== 2)
        return null;
    const [primary, additional] = metricSources;
    // Sources run under their explore's name, as the editor names them
    const primaryName = primary.metricQuery.exploreName;
    const additionalName = additional.metricQuery.exploreName === primaryName
        ? `${primaryName}_2`
        : additional.metricQuery.exploreName;
    const idBySourceId = {
        [primary.id]: primaryName,
        [additional.id]: additionalName,
    };
    const fieldIdByAiFieldId = {};
    metricSources.forEach((source) => {
        const canonicalId = idBySourceId[source.id];
        [
            ...source.metricQuery.metrics,
            ...source.metricQuery.tableCalculations.map((calculation) => calculation.name),
        ].forEach((column) => {
            fieldIdByAiFieldId[mergedColumnId(source.id, column)] =
                mergedColumnId(canonicalId, column);
        });
    });
    // A key column is named after the primary's field, as the editor names it
    const joinKey = mergeQuery.joinKey.map((part) => {
        const name = part.fieldIdBySourceId[primary.id];
        fieldIdByAiFieldId[mergedColumnId(MERGE_TABLE_NAME, part.name)] =
            mergedColumnId(MERGE_TABLE_NAME, name);
        return {
            name,
            fieldIdBySourceId: Object.fromEntries(Object.entries(part.fieldIdBySourceId).map(([sourceId, fieldId]) => [idBySourceId[sourceId], fieldId])),
        };
    });
    return {
        mergeQuery: {
            sources: metricSources.map((source) => ({
                id: idBySourceId[source.id],
                metricQuery: source.metricQuery,
            })),
            joinKey,
            joinType: mergeQuery.joinType,
            tableCalculations: mergeQuery.tableCalculations,
            limit: mergeQuery.limit,
        },
        fieldIdByAiFieldId,
    };
};
/**
 * Replaces field-id references throughout a config value: string values and
 * object keys that exactly match a map entry are renamed, everything else is
 * left alone. Field ids only ever appear as whole strings, so exact matching
 * cannot corrupt labels or SQL.
 */
export const remapFieldIdsDeep = (value, fieldIdMap) => {
    if (typeof value === 'string') {
        return (fieldIdMap[value] ?? value);
    }
    if (Array.isArray(value)) {
        return value.map((item) => remapFieldIdsDeep(item, fieldIdMap));
    }
    if (value !== null && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [
            fieldIdMap[key] ?? key,
            remapFieldIdsDeep(item, fieldIdMap),
        ]));
    }
    return value;
};
//# sourceMappingURL=canonicalizeAiMerge.js.map